import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { identifyUser, resetUser } from '../services/analytics';

// Utiliser l'URL backend depuis les variables d'environnement
const getBackendURL = () => {
  // Priorité 1: Variable d'environnement (définie par Emergent en production)
  if (process.env.REACT_APP_BACKEND_URL) {
    console.log('🌐 Backend URL depuis env:', process.env.REACT_APP_BACKEND_URL);
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback pour développement local
  const hostname = window.location.hostname;
  if (hostname === 'localhost') {
    return 'http://localhost:8001';
  }
  
  // Fallback: utiliser le même domaine que le frontend
  const protocol = window.location.protocol;
  return `${protocol}//${hostname}`;
};

const BACKEND_URL = getBackendURL();
console.log('🌐 Backend URL configuré:', BACKEND_URL);

const AuthContext = createContext();

// API Instance with automatic token handling
const api = axios.create({
  baseURL: BACKEND_URL + '/api',
  timeout: 600000, // 10 minutes pour la génération d'histoires avec images
});

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    accessToken: null,
    refreshToken: null,
  });

  // Setup axios interceptors
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔐 Token attached to request:', token.substring(0, 20) + '...');
        } else {
          console.warn('⚠️ No token found for request');
        }
        return config;
      },
      (error) => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          console.log('🔄 Token expired, attempting refresh...');
          try {
            await refreshAccessToken();
            const newToken = localStorage.getItem('access_token');
            if (newToken) {
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return api(error.config);
            }
          } catch (refreshError) {
            console.error('❌ Token refresh failed:', refreshError);
            await clearAuthData();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const clearAuthData = async () => {
    console.log('🧹 Clearing auth data...');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessToken: null,
      refreshToken: null,
    });
  };

  const initializeAuth = async () => {
    try {
      console.log('🔄 Initializing authentication...');
      
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      console.log('📱 Tokens found:', { hasAccess: !!accessToken, hasRefresh: !!refreshToken });

      if (accessToken && refreshToken) {
        setAuthState(prev => ({
          ...prev,
          accessToken,
          refreshToken,
        }));

        try {
          console.log('🔐 Verifying token...');
          const response = await Promise.race([
            api.get('/auth/me', {
              headers: { Authorization: `Bearer ${accessToken}` },
              timeout: 45000
            }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Auth verification timeout')), 45000)
            )
          ]);

          console.log('✅ Token valid, user authenticated');
          setAuthState(prev => ({
            ...prev,
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
          }));
        } catch (error) {
          console.log('❌ Token invalid, clearing auth data');
          await clearAuthData();
        }
      } else {
        console.log('📭 No tokens found, user needs to login');
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          isAuthenticated: false,
        }));
      }
    } catch (error) {
      console.error('💥 Auth initialization error:', error);
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        accessToken: null,
        refreshToken: null,
      });
    }
  };

  const login = async (credentials) => {
    try {
      console.log('🔐 Starting login process...');
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const response = await api.post('/auth/token', credentials);
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        accessToken: access_token,
        refreshToken: refresh_token,
      });

      console.log('✅ Login successful');
      // Identify user in PostHog
      if (user?.id) identifyUser(user.id, { email: user.email, name: user.first_name });
    } catch (error) {
      console.error('❌ Login failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const register = async (credentials) => {
    try {
      console.log('📝 Starting registration process...');
      setAuthState(prev => ({ ...prev, isLoading: true }));

      const response = await api.post('/auth/register', credentials);
      const { access_token, refresh_token, user } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
        accessToken: access_token,
        refreshToken: refresh_token,
      });

      console.log('✅ Registration successful');
    } catch (error) {
      console.error('❌ Registration failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('👋 Logging out...');
      await clearAuthData();
      resetUser();
      console.log('✅ Logout complete');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken,
      });

      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      
      setAuthState(prev => ({
        ...prev,
        accessToken: access_token,
      }));

      console.log('✅ Token refreshed successfully');
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      await clearAuthData();
      throw error;
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  const value = {
    ...authState,
    login,
    register,
    logout,
    refreshAccessToken,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};